import React, { useState, useEffect } from "react";
import {
  Flex,
  Heading,
  Button,
  Spinner,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import {
  textSecondary,
  primary,
  primaryAlt,
} from "../../../../utils/colors.js";
import { useTranslation } from "react-i18next";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { getMonthlyTreatmentPlanStatisticsStacked } from "../../../../actions/statisticActions.js";
import { useDispatch, useSelector } from "react-redux";
import { subMonths, format } from "date-fns";
import { TbCoinEuro } from "react-icons/tb";
import useThemeColors from "../../../../hooks/useThemeColors.js";
import dayjs from "dayjs";
import TreatmentPlansMonthlyChart from "./TreatmentPlansMonthlyChart.js";
import { useParams } from "react-router-dom";
import useIsMobile from "../../../../hooks/useIsMobile.js";

// Helper function to filter last 6 months
const getLast6Months = (monthlyData) => {
  const today = new Date();
  const sixMonthsAgo = subMonths(today, 5); // Go back 5 months to include the current month
  const formattedSixMonthsAgo = format(sixMonthsAgo, "yyyy-MM");

  // Filter data for last 6 months
  return monthlyData.filter((item) => {
    const itemDate = new Date(item.year, item.month - 1); // month is 1-based
    const formattedItemDate = format(itemDate, "yyyy-MM");
    return formattedItemDate >= formattedSixMonthsAgo;
  });
};

const getLastFullYear = (monthlyData) => {
  const today = new Date();
  const twelveMonthsAgo = subMonths(today, 11); // Go back 11 months to include the current month
  const formattedTwelveMonthsAgo = format(twelveMonthsAgo, "yyyy-MM");

  // Filter data for last 12 months
  return monthlyData.filter((item) => {
    const itemDate = new Date(item.year, item.month - 1); // month is 1-based

    const formattedItemDate = format(itemDate, "yyyy-MM");
    return formattedItemDate >= formattedTwelveMonthsAgo;
  });
};

const DashboardMonthlyStacked = ({ currency }) => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // COLORS
  const { whiteMode, textPrimaryMode, backgroundLightMode } = useThemeColors();

  // STATE
  const [showFullYear, setShowFullYear] = useState(false);
  const [showInPrice, setShowInPrice] = useState(false);

  // const currencySymbol = useCurrencySymbol(currency);

  // REDUX
  const dispatch = useDispatch();

  const statisticMonthlyTreatmentPlanStacked = useSelector(
    (state) => state.statisticMonthlyTreatmentPlanStacked
  );
  const {
    loading,
    error,
    monthlyTreatmentPlans = [],
  } = statisticMonthlyTreatmentPlanStacked;

  useEffect(() => {
    dispatch(getMonthlyTreatmentPlanStatisticsStacked("", slug));
  }, [dispatch, slug]);

  const lastSixMonthsData = showFullYear
    ? getLastFullYear(monthlyTreatmentPlans)
    : getLast6Months(monthlyTreatmentPlans);

  if (loading)
    return (
      <Flex
        w="100%"
        bg={whiteMode}
        p="1rem 1.5rem"
        borderRadius="1rem"
        h="567px"
        minH="567px"
        maxH="567px"
        align="center"
        justify="center"
      >
        <Spinner size="lg" color={primary} />
      </Flex>
    );
  if (error) return <div>Error loading data</div>;
  if (!monthlyTreatmentPlans.length) return <div>No data available</div>;

  // console.log("monthlyTreatmentPlans:", monthlyTreatmentPlans);
  // console.log("lastSixMonthsData:", lastSixMonthsData);
  // console.log("showFullYear:", showFullYear);

  return (
    <Flex
      w="100%"
      bg={whiteMode}
      borderRadius="1rem"
      p="1rem 1.5rem"
      direction="column"
    >
      <Flex w="100%" align="center" justify="space-between" minH="2rem">
        <Flex align="center">
          <Heading
            fontSize="1.125rem"
            fontWeight="600"
            color={textPrimaryMode}
            whiteSpace="nowrap"
          >
            {t("dashboard.monthlyTreatmentPlans")}
          </Heading>
        </Flex>

        <Flex align="center" justify="flex-end" gap="0.5rem" w="100%">
          <IconButton
            size="sm"
            bg={showInPrice ? primaryAlt : backgroundLightMode}
            color={showInPrice ? whiteMode : textSecondary}
            borderRadius="1rem"
            onClick={() => setShowInPrice(!showInPrice)}
            _hover={{ bg: primaryAlt, color: whiteMode }}
          >
            <Icon as={TbCoinEuro} fontSize="0.875rem" />
          </IconButton>

          {!isMobile && (
            <>
              <Button
                size="sm"
                fontSize="0.8rem"
                bg={backgroundLightMode}
                color={textSecondary}
                borderRadius="1rem"
                onClick={() => setShowFullYear(!showFullYear)}
                isDisabled
              >
                {showFullYear
                  ? t("dashboard.last12Months")
                  : t("dashboard.last6Months")}
              </Button>
              <Button
                size="sm"
                fontSize="0.8rem"
                bg={backgroundLightMode}
                color={textSecondary}
                borderRadius="1rem"
                isDisabled
              >
                {dayjs().format("YYYY")}
              </Button>
            </>
          )}
        </Flex>
      </Flex>

      <Flex w="100%" h="100%" position="relative" direction="column">
        <Flex w="100%" position="relative">
          <ParentSize>
            {({ width }) => (
              <TreatmentPlansMonthlyChart
                monthlyTreatmentPlans={lastSixMonthsData}
                width={width}
                height={253}
                showInPrice={showInPrice}
                setShowInPrice={setShowInPrice}
                currency={currency}
              />
            )}
          </ParentSize>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default DashboardMonthlyStacked;
